// Locker Room — the layered model, theme side.
//
// THE MODEL (agreed with Golf Poser, 10 Sep 2026). Favourites and outfits are
// layered, not parallel:
//
//   1. Saving anywhere on the site puts the item in Favourites only.
//   2. Favourites is the rail — the pool an outfit is drawn from.
//   3. An outfit is built only from the rail, never from the catalogue.
//   4. If the rail hasn't got it, the shopper LEAVES to go shopping — a full
//      collection page, not a panel. Graeme: "it doesn't really make logical
//      sense to be shopping shirts from such a small little div on the right
//      hand side."
//
// WHY THIS IS IN THE THEME AND NOT THE APP. Enlist is live on several stores
// and this is one client's model, so it does not belong in the shared
// extension. The app already publishes everything needed to build it from
// outside: window.QFL.lists() gives the pool (is_primary) and every outfit's
// items, QFL.items() gives each saved variant with its collection_handles,
// and QFL.settings() gives the merchant's grouping collections. The bridge's
// own comment calls that "enough for a theme to render its own grid with no
// worker call", which is exactly what this does.
//
// THE TWO PLACES IT REACHES PAST THE BRIDGE, both deliberate:
//
//   - The outfit category chips are rendered BY THE APP into a theme slot
//     ([data-qfl-outfit-chips]), so changing what they look like and what
//     they do means re-asserting over app-written DOM. Hence the observer
//     and the capture-phase click handler below. If the app ever stops
//     rendering chips there, dropChipCounts simply finds nothing.
//
//   - QFL.add() cannot target a list: it routes through the app's own
//     targeting, which under the pinned model always lands in Favourites —
//     the exact opposite of "add this to THIS outfit". So adding from the
//     panel POSTs to the worker's public add endpoint with the outfit's
//     share_token, then asks the app to re-read state. The public surface is
//     documented as open by design; this is not a way round anything.
//
// DEPENDS ON the app reporting is_primary correctly. Until Sep 2026 a
// logged-in shopper's lists were seeded from Liquid without that flag, so
// pool() found nothing and every surface here would fall back to "no pool".

(() => {
  const ISLAND = 'qfl-global-config';
  const CHIP = '[data-qfl-outfit-chip]';

  let cfg = null;
  function config() {
    if (cfg) return cfg;
    try { cfg = JSON.parse(document.getElementById(ISLAND).textContent); } catch { cfg = {}; }
    return cfg;
  }

  const QFL = () => window.QFL;
  const categories = () => (QFL()?.settings?.().grouping_collections) || [];

  // The pool: the shopper's pinned list. Null when they have not saved
  // anything yet, or when the shop is not running the pinned model — every
  // surface below degrades to doing nothing rather than guessing.
  function pool() {
    try { return (QFL().lists() || []).find((l) => l.is_primary) || null; } catch { return null; }
  }

  // Pool entries with their snapshots attached. QFL.items() is the union of
  // every list, so it is filtered down to the pool's own variants rather than
  // trusted as-is: under this model the two are the same set, but only while
  // the data is clean, and a stale outfit item must not appear in Favourites.
  function poolEntries() {
    const p = pool();
    if (!p) return [];
    const mine = new Set((p.items || []).map((i) => i.variant_gid));
    try { return (QFL().items() || []).filter((it) => mine.has(it.variant_gid)); } catch { return []; }
  }

  const inCategory = (it, handle) => (it.collection_handles || []).includes(handle);
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
  const routeRoot = () => (window.Shopify?.routes?.root) || '/';

  // ── The Favourites rail: category chips ──────────────────────
  //
  // "Trousers (20) +", linking to that collection page. This design used to
  // sit on the outfit rows; the swap is the point. Fred's rule in the room:
  // a plus means "go and get more", so it belongs only to the control that
  // leaves for the catalogue, and one design must not do two different
  // things.
  //
  // Never disabled, unlike the filter pills this replaces. A category with
  // nothing saved is precisely when a shopper wants to go and find some —
  // disabling it was the dead end Andy hit ("these bubbles aren't doing
  // anything, they're greyed out").
  function renderRailChips() {
    const host = document.querySelector('[data-qfl-crs-categories]');
    if (!host) return;
    const cats = categories();
    if (!cats.length) { host.hidden = true; return; }
    const entries = poolEntries();
    host.hidden = false;
    host.innerHTML = cats.map((c) => {
      const n = entries.filter((it) => inCategory(it, c.handle)).length;
      return `<a class="qfl-wl-crs__cat" href="${esc(routeRoot())}collections/${esc(c.handle)}">`
        + `<span class="qfl-wl-crs__cat-label">${esc(c.title || c.handle)} (${n})</span>`
        + '<span class="qfl-wl-crs__cat-plus" aria-hidden="true">+</span></a>';
    }).join('');
  }

  // ── The outfit rows: bubbles, not plus-chips ─────────────────
  //
  // The app renders "Trousers (0) +". The bubble carries neither the count
  // nor the plus, so the plus goes in CSS and the count has to go here — it
  // is inside the same span as the title. Re-run on every app re-render.
  function dropChipCounts(scope) {
    (scope || document).querySelectorAll(`${CHIP} span:first-child`).forEach((el) => {
      const cleaned = el.textContent.replace(/\s*\(\d+\)\s*$/, '');
      if (cleaned !== el.textContent) el.textContent = cleaned;
    });
  }

  // ── Outfit cards: which size was saved ───────────────────────
  //
  // An outfit item is a VARIANT, not a product — the shopper saved the 32R,
  // and an outfit is a set of things they intend to buy in those sizes. The
  // card is the site-wide theme card, which knows nothing about that, so it
  // showed eight size pills with none marked and no size anywhere in the
  // text. You could not tell what was in your own outfit without opening it.
  //
  // Everything needed is already inside the card: <product-card> carries
  // data-size-index, and .product-variants-json lists every variant with its
  // options. So this reads the saved variant id off the app's <li> and looks
  // its size up locally — no extra request per card.
  //
  // VISUAL ONLY, ON PURPOSE. It adds its own class rather than the theme's
  // .selected, and never clicks. product-card.js reads .selected once at
  // init and derives its action button from it, and the app watches cards
  // for variant changes and writes a swap when one happens — driving either
  // from here risks a write on every render. A class and a line of text
  // touch neither: the app's watcher observes data-variant-id only.
  //
  // Picking a different size still works as before: the card selects it, the
  // app swaps the saved variant, the outfit re-renders, and this re-marks it.
  //
  // WHY IT RE-RUNS ON EVERY RENDER. The app's reconcileCards keeps a card only
  // when its outerHTML is byte-identical to the fresh render, and theme cards
  // never are once they have booted: the hanger ships with a `loading`
  // attribute that wishlist-enlist.js strips. So every outfit card is
  // replaced on every state change, before this code touches anything, and
  // the marks below have to be re-applied each time — which the observer
  // does. The marks add no churn of their own; that replacement already
  // happens.
  function savedSizeOf(li) {
    const gid = li.getAttribute('data-variant-gid');
    if (!gid) return null;
    const id = Number(String(gid).split('/').pop());
    const card = li.querySelector('product-card, .product-card');
    if (!card) return null;
    const idx = parseInt(card.dataset.sizeIndex ?? '-1', 10);
    if (Number.isNaN(idx) || idx < 0) return null;   // no size option: a bag, a glove
    let variants = [];
    try { variants = JSON.parse(card.querySelector('.product-variants-json')?.textContent || '[]'); } catch { return null; }
    const v = variants.find((x) => Number(x.id) === id);
    if (!v) return null;
    return (Array.isArray(v.options) ? v.options[idx] : v[`option${idx + 1}`]) || null;
  }

  // Every Locker Room surface that shows a SAVED variant: the outfits, the
  // Favourites rail (and its copy in the slideout — both are
  // .qfl-wl-crs__track), and the pool panel, where the size matters most
  // because "Add to outfit" adds exactly that variant. Fred, 2026-09-24:
  // Favourites should say which variant it holds, the same as an outfit.
  const SAVED_ITEM = [
    '.qfl-wl-outfit__grid [data-qfl-shared-item][data-variant-gid]',
    '.qfl-wl-crs__track [data-qfl-shared-item][data-variant-gid]',
    '.qfl-lr-panel [data-variant-gid]',
  ].join(', ');

  function markSavedSizes() {
    document.querySelectorAll(SAVED_ITEM).forEach((li) => {
      const size = savedSizeOf(li);
      li.querySelectorAll('.size-option-button').forEach((b) => {
        b.classList.toggle('qfl-lr-size--saved', !!size && b.dataset.sizeVal === size);
      });
      // Idempotent: only writes when the text would change, so a re-run from
      // the observer does not churn the DOM it is watching.
      const existing = li.querySelector('.qfl-lr-size');
      if (!size) { existing?.remove(); return; }
      const text = `Size: ${size}`;
      if (existing) { if (existing.textContent !== text) existing.textContent = text; return; }
      const title = li.querySelector('a.product-title');
      if (title) title.insertAdjacentHTML('afterend', `<p class="qfl-lr-size">${esc(text)}</p>`);
    });
  }

  // ── The pool panel ───────────────────────────────────────────
  //
  // Opens where the fetched collection listing used to go, and renders the
  // shopper's own saved items in that category. No filters, no sort, no
  // load-more: Graeme, when Fred raised it — "that's fine anyway, ultimately
  // you're going to have maximum like 10 in there."
  function slideout() { return document.querySelector('[data-qfl-slideout]'); }

  function openSlideout() {
    const el = slideout();
    if (!el) return null;
    // The app wires close on ITS opener, which has not necessarily run. Own
    // flag so this is wired once and never twice.
    if (el.dataset.lockerRoomWired !== '1') {
      el.dataset.lockerRoomWired = '1';
      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-qfl-slideout-close]')
          || (e.target instanceof Element && e.target.hasAttribute('data-qfl-slideout-backdrop'))) {
          closeSlideout();
        }
      });
    }
    el.classList.add('qfl-wl-slideout--open', 'qfl-wl-slideout--collection');
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('qfl-wl-slideout-lock');
    return el;
  }

  function closeSlideout() {
    const el = slideout();
    if (!el) return;
    el.classList.remove('qfl-wl-slideout--open', 'qfl-wl-slideout--collection');
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('qfl-wl-slideout-lock');
    const host = el.querySelector('[data-qfl-slideout-collection]');
    if (host) { host.hidden = true; host.innerHTML = ''; }
  }

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (slideout()?.classList.contains('qfl-wl-slideout--open')) closeSlideout();
  });

  // The card IS the theme card, fetched over the Section Rendering API —
  // the same source the app uses for the rail, so the panel cannot drift
  // from the rest of the page.
  const cardCache = new Map();
  async function themeCard(handle, variantGid) {
    const variantId = variantGid ? String(variantGid).split('/').pop() : '';
    const key = `${handle}::${variantId}`;
    if (cardCache.has(key)) return cardCache.get(key);
    const p = (async () => {
      try {
        const qs = variantId ? `&variant=${encodeURIComponent(variantId)}` : '';
        const res = await fetch(`${routeRoot()}products/${encodeURIComponent(handle)}?section_id=product-card${qs}`);
        if (!res.ok) return null;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        return doc.querySelector('product-card, .product-card')?.outerHTML || null;
      } catch { return null; }
    })();
    cardCache.set(key, p);
    return p;
  }

  async function openPoolPanel(handle, title, outfitToken) {
    const el = openSlideout();
    if (!el) return false;
    const host = el.querySelector('[data-qfl-slideout-collection]');
    if (!host) return false;

    const titleEl = el.querySelector('[data-qfl-slideout-title]');
    if (titleEl && title) titleEl.textContent = title;

    host.hidden = false;
    host.setAttribute('aria-busy', 'true');
    host.innerHTML = '';

    const entries = poolEntries().filter((it) => inCategory(it, handle));

    // THE EMPTY STATE IS A PROMPT, NOT A DEAD END. Graeme would have had the
    // chip skip straight to the collection to save a click; Fred argued the
    // panel-then-link keeps the interaction predictable, since every chip
    // then does the same thing whatever is behind it. Agreed: panel, with a
    // way out to the category.
    if (!entries.length) {
      host.removeAttribute('aria-busy');
      host.innerHTML = '<div class="qfl-lr-empty">'
        + `<p class="qfl-lr-empty__text">You haven't saved any ${esc((title || '').toLowerCase())} yet.</p>`
        + `<a class="button-2 qfl-lr-empty__cta" href="${esc(routeRoot())}collections/${esc(handle)}">Browse ${esc(title)}</a>`
        + '</div>';
      return true;
    }

    const cards = await Promise.all(entries.map(async (it) => {
      const html = await themeCard(it.product_handle, it.variant_gid);
      // No card means the product is gone from the storefront. Show it
      // nowhere rather than rendering a titleless, priceless ghost.
      if (!html) return '';
      return `<li class="qfl-lr-panel__item" data-variant-gid="${esc(it.variant_gid)}">${html}`
        + `<button type="button" class="button-2 qfl-lr-panel__add" data-qfl-lr-add`
        + ` data-variant-gid="${esc(it.variant_gid)}" data-product-gid="${esc(it.product_gid)}">Add to outfit</button></li>`;
    }));

    host.removeAttribute('aria-busy');
    host.innerHTML = `<ul class="qfl-lr-panel" data-outfit-token="${esc(outfitToken || '')}">${cards.join('')}</ul>`;
    syncPanelButtons();
    markSavedSizes();
    return true;
  }

  // Say which of these are already in the outfit.
  //
  // The button used to be set to "Added" only by the click that added it, so
  // opening a category showed everything as addable even when the outfit
  // already held it — the shopper's own outfit contents were invisible from
  // the panel that builds it, and the only way to find out was to add a
  // duplicate. Reported off the preview, 2026-09-23.
  //
  // Derived from state rather than remembered, so it is also correct after a
  // refresh, after a removal elsewhere on the page, and on re-opening the
  // same category later.
  function syncPanelButtons() {
    const list = document.querySelector('[data-outfit-token]');
    if (!list) return;
    const token = list.getAttribute('data-outfit-token');
    const outfit = (() => {
      try { return (QFL().lists() || []).find((l) => l.share_token === token) || null; } catch { return null; }
    })();
    const held = new Set((outfit?.items || []).map((i) => i.variant_gid));
    list.querySelectorAll('[data-qfl-lr-add]').forEach((btn) => {
      const inOutfit = held.has(btn.getAttribute('data-variant-gid'));
      btn.textContent = inOutfit ? 'Added' : 'Add to outfit';
      btn.classList.toggle('qfl-lr-panel__add--added', inOutfit);
      if (inOutfit) btn.setAttribute('disabled', '');
      else btn.removeAttribute('disabled');
    });
  }

  // Make the app re-read its own lists after we have written one behind its
  // back.
  //
  // QFL.rescan() does NOT do this — it only re-emits qfl:rescan, which
  // re-runs each module's init(), and boot() is guarded by a _booted flag.
  // The function that actually refetches, reloadWishlists(), is exported by
  // qfl-core.js but is not on the bridge. Re-importing the module by the URL
  // the page already loaded returns the SAME live instance (one module record
  // per URL), so this drives the real state rather than a second copy of it.
  //
  // Reaching past the bridge, and worth saying so. A page reload is the
  // honest fallback: slower, but the shopper never sees a stale outfit.
  async function refreshAppState() {
    try {
      const url = [...document.querySelectorAll('script[src]')]
        .map((s) => s.src).find((s) => /qfl-core\.js/.test(s));
      if (!url) throw new Error('no qfl-core on the page');
      const mod = await import(url);
      if (typeof mod.reloadWishlists !== 'function') throw new Error('reloadWishlists is gone');
      await mod.reloadWishlists();
    } catch {
      window.location.reload();
    }
  }

  // Add straight into the outfit the chip came from. See the header: the
  // bridge cannot express "this list", so this is the public add endpoint
  // with an explicit share_token, followed by a refresh so the app re-reads
  // its own state and every surface on the page repaints.
  async function addToOutfit(btn) {
    const list = btn.closest('[data-outfit-token]');
    const token = list?.getAttribute('data-outfit-token');
    if (!token) return;
    const c = config();
    if (!c.worker_url || !c.shop) return;
    btn.setAttribute('disabled', '');
    try {
      const res = await fetch(`${c.worker_url}/api/public/wishlist/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop: c.shop,
          share_token: token,
          variant_gid: btn.getAttribute('data-variant-gid'),
          product_gid: btn.getAttribute('data-product-gid') || undefined,
          customer_gid: c.customer_gid || undefined,
          qty: 1,
        }),
      });
      if (res.ok) {
        // Optimistic, then corrected from state once the app has re-read —
        // so this agrees with every other item in the panel instead of being
        // the one button that knows something the rest do not.
        btn.textContent = 'Added';
        btn.classList.add('qfl-lr-panel__add--added');
        await refreshAppState();
        syncPanelButtons();
      } else {
        btn.removeAttribute('disabled');
      }
    } catch {
      btn.removeAttribute('disabled');
    }
  }

  // ── Wiring ───────────────────────────────────────────────────

  // CAPTURE PHASE, and it has to be. The app listens for this same chip at
  // document level in wireCreate(); without capture its handler would run
  // first and fetch the collection, which is the behaviour being replaced.
  document.addEventListener('click', (e) => {
    const chip = e.target.closest(CHIP);
    if (!chip) return;
    if (!pool()) return;            // no pool — leave the app's behaviour alone
    e.preventDefault();
    e.stopPropagation();
    const outfit = chip.closest('[data-qfl-outfit]')?.getAttribute('data-share-token') || '';
    openPoolPanel(
      chip.getAttribute('data-qfl-outfit-chip'),
      chip.getAttribute('data-qfl-chip-title') || '',
      outfit,
    );
  }, true);

  document.addEventListener('click', (e) => {
    const add = e.target.closest('[data-qfl-lr-add]');
    if (add) { e.preventDefault(); addToOutfit(add); }
  });

  function paint() {
    renderRailChips();
    dropChipCounts();
    markSavedSizes();
    // The panel can be open while the outfit changes underneath it — an item
    // crossed off the outfit behind the overlay has to become addable again.
    syncPanelButtons();
  }

  // The app re-renders the chips on every state change, so this has to run
  // after it rather than once. The observer catches the re-render; the event
  // catches data arriving.
  //
  // WATCHING THE ADDED NODES, not the mutation target. An outfit row is built
  // by cloning a <template> and filling it while it is still OUT of the
  // document, so the innerHTML write on the chips container is never seen —
  // the only mutation that reaches here is the finished row being appended,
  // whose target is the outfits HOST. closest() walks up from there and finds
  // nothing, because the chips are below it, not above. That left every
  // outfit row still reading "Accessories (0)" while the Create New Outfit
  // row, which is filled in place, came out correct — which is exactly how
  // this showed up on the preview theme.
  //
  // Re-entry is bounded: dropChipCounts only writes when the text actually
  // changes, so the mutation it causes finds nothing to do on the next pass.
  function observe() {
    const obs = new MutationObserver((records) => {
      for (const r of records) {
        for (const n of r.addedNodes) {
          if (n.nodeType !== 1) continue;
          // Outfit cards arrive as whole rows (the template clone again) or
          // as a card swapped in place, so either shape re-marks the sizes.
          // The <p> this adds does not match, so it cannot re-trigger itself.
          if (n.matches?.('[data-qfl-shared-item]') || n.querySelector?.('.qfl-wl-outfit__grid [data-qfl-shared-item], [data-qfl-shared-item] product-card')) {
            markSavedSizes();
          }
          if (n.matches?.(CHIP) || n.querySelector?.(CHIP)) { dropChipCounts(); return; }
        }
        if (r.target.nodeType === 1 && r.target.closest?.('[data-qfl-outfit-chips]')) {
          dropChipCounts(r.target);
          return;
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  async function init() {
    if (!window.QFL) return;
    try { await window.QFL.ready; } catch { return; }
    paint();
    observe();
    document.addEventListener('qfl:wishlist:changed', paint);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('qfl:ready', init, { once: true });
})();
